import { useState, useEffect } from 'react'
import { useCustomFetch } from './useCustomFetch'

/**
 * Custom hook to efficiently load and cache video URLs for multiple meetings
 * @param evidence Array of evidence items containing meeting_ids
 * @param meetingMap Optional map of meetings for additional validation
 * @returns Object mapping meeting IDs to their video URLs
 */
export function useVideoUrls(evidence: any[], meetingMap?: Record<string, any>) {
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const customFetch = useCustomFetch()

  useEffect(() => {
    // Skip if no evidence
    if (!evidence?.length) return

    const fetchAllUrls = async () => {
      setIsLoading(true)

      // Extract unique meeting IDs
      const meetingIds = [...new Set(evidence.filter((note) => note.meeting_id).map((note) => note.meeting_id))]

      if (meetingIds.length === 0) {
        setIsLoading(false)
        return
      }

      // First check cache and add any valid cached URLs to our state
      const cachedUrls: Record<string, string> = {}
      const urlsToFetch: string[] = []

      meetingIds.forEach((meetingId) => {
        const cachedUrl = sessionStorage.getItem(`meeting-url-${meetingId}`)
        const cachedTimestamp = sessionStorage.getItem(`meeting-url-${meetingId}-timestamp`)

        // URLs are valid for ~1 hour (based on Firebase's generateSignedUrl implementation)
        // We'll use 50 minutes to be safe
        if (cachedUrl && cachedTimestamp) {
          const timestamp = parseInt(cachedTimestamp, 10)
          const now = Date.now()
          const expiryTime = 50 * 60 * 1000 // 50 minutes in milliseconds

          if (now - timestamp < expiryTime) {
            // URL is still valid, add to our state
            cachedUrls[meetingId] = cachedUrl
          } else {
            urlsToFetch.push(meetingId)
          }
        } else {
          urlsToFetch.push(meetingId)
        }
      })

      // Update state with cached URLs
      if (Object.keys(cachedUrls).length > 0) {
        setVideoUrls((prev) => ({
          ...prev,
          ...cachedUrls,
        }))
      }

      // If no URLs to fetch, we're done
      if (urlsToFetch.length === 0) {
        setIsLoading(false)
        return
      }

      try {
        // Use the new batch endpoint to fetch URLs
        const response = await customFetch('/getMeetUrlsBatch', 'POST', { meetingIds: urlsToFetch })

        if (!response.error && response.urls) {
          // Cache the results
          Object.entries(response.urls).forEach(([meetingId, url]) => {
            sessionStorage.setItem(`meeting-url-${meetingId}`, url as string)
            sessionStorage.setItem(`meeting-url-${meetingId}-timestamp`, Date.now().toString())
          })

          // Update state with new URLs
          setVideoUrls((prev) => ({
            ...prev,
            ...response.urls,
          }))
        } else {
          // If batch fails, fall back to individual requests
          console.warn('Batch URL fetch failed, falling back to individual requests')
          await fetchUrlsIndividually(urlsToFetch)
        }
      } catch (error) {
        console.error('Error getting batch URLs:', error)
        // Fall back to individual requests
        await fetchUrlsIndividually(urlsToFetch)
      }

      setIsLoading(false)
    }

    const fetchUrlsIndividually = async (meetingIds: string[]) => {
      // Process in batches of 5 to avoid overwhelming the server
      const batchSize = 5
      const batches = []

      for (let i = 0; i < meetingIds.length; i += batchSize) {
        batches.push(meetingIds.slice(i, i + batchSize))
      }

      const newUrls: Record<string, string> = {}

      for (const batch of batches) {
        // Fetch URLs in parallel
        const urlPromises = batch.map(async (meetingId) => {
          try {
            const res = await customFetch(`/getMeetUrl/${meetingId}`, 'GET', null)
            if (!res.error && res.url) {
              // Cache the result
              sessionStorage.setItem(`meeting-url-${meetingId}`, res.url)
              sessionStorage.setItem(`meeting-url-${meetingId}-timestamp`, Date.now().toString())
              return { meetingId, url: res.url }
            }
          } catch (error) {
            console.error(`Error getting URL for meeting ${meetingId}:`, error)
          }
          return { meetingId, url: null }
        })

        const results = await Promise.all(urlPromises)

        // Add valid results to our URL map
        results.forEach(({ meetingId, url }) => {
          if (url) newUrls[meetingId] = url
        })
      }

      setVideoUrls((prev) => ({
        ...prev,
        ...newUrls,
      }))
    }

    fetchAllUrls()
  }, [evidence, customFetch])

  return { videoUrls, isLoading }
}
